import { CSSProperties, useState, useEffect, useContext } from 'react'
import useScreen from 'Hooks/useScreen'
import useBrand from 'Hooks/useBrand'
import useMaestroChat, { MaestroClientEvents } from 'Hooks/useMaestroChat'
import useCampaignBuilder, { Caption, CreativeSelectorPost, AgentCampaignData, transformPostsToCreativeFormat } from 'Hooks/useCampaignBuilder' // Added AgentCampaignData
import { styx, clsx, formatNumberToDisplayType, showToast } from 'modules/Utils'
import { AdPlatform, PLAYBOOK_TYPE_INFO, PlaybookType, PlanType } from 'modules/Const'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import Icon from 'elements/Icon'
import ButtonSymphony from 'elements/ButtonSymphony'
import CardSymphony from 'elements/CardSymphony'
import TagSymphony from 'elements/TagSymphony'
import Banner from 'elements/Banner'
import AdPreview from 'elements/AdPreview'
import TabsSymphony, { TabItem } from 'elements/TabsSymphony'
import ModalSymphony from 'elements/ModalSymphony'
import InputSymphony, { SelectOptionItem } from 'elements/InputSymphony'
import Axios from 'helpers/Interceptor'
import AssistantWorkspaceActions, { ActionButton } from './AssistantWorkspaceActions'
import { WorkspaceTheme } from './AssistantWorkspaceWrapper'
import { CallToActionType } from 'modules/Campaign'
import { MediaType } from 'modules/Types'
import { AssetPlatformType } from 'components/CampaignViews/AdCreativeSelectorCampaignView'
import useFacebookConnections from 'Hooks/useFacebookConnections'
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { simulateCampaignCheckout_api, submitCampaignDirect_api } from 'api/agentApi';
import useMaestroTasks from 'Hooks/useMaestroTasks';
import { useAuth0 } from '@auth0/auth0-react';
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext';
import UnifiedPaymentForm from 'elements/UnifiedPaymentForm';

import CampaignCheckoutView from 'elements/CampaignCheckoutView'; // Import CampaignPlanSelectionView
import type { ConfirmPaymentResponse, PaymentMethodResponse, DirectCampaignChargeResponse } from 'api/paymentsV2Api';
import { chargeCampaignDirect_api, getDefaultPaymentMethod_api } from 'api/paymentsV2Api';
import useSignupFlow, { WorkspaceType } from 'Hooks/useSignupFlow'

// Interface definitions based on the updated API structure

/** Interface for TikTok identity info */
export interface TikTokUserInfo {
    identity_type: string;
    identity_id: string;
    identity_display_name: string;
    identity_info?: {
        avatar_url?: string;
        follower_count?: number;
        following_count?: number;
        likes_count?: number;
        video_count?: number;
    };
}


/** Interface for TikTok video information */
export interface TikTokVideoInfo {
    file_name: string;
    poster_url: string;
    bit_rate: number;
    size: number;
    width: number;
    height: number;
    signature: string;
    duration: number;
    preview_url: string;
    video_id: string;
}

export interface TikTokItemInfo {
    text: string;
    anchor_list: null | any; // TODO: Define specific type
    stitch_original_item_id: string;
    auth_code: string;
    duet_original_item_id: string;
    item_id: string;
    item_type: 'VIDEO' | 'IMAGE' | 'CAROUSEL';
    is_multi_duet_stitch: boolean;
    landing_page_url?: string;
    mentioned_item_ids: null | any; // TODO: Define specific type
}

/**
 * Describes the source of a single creative asset for the campaign.
 * This is based on the agent's discovery process in the prompt.
 */
export type CreativeSource =
    | {
        type: 'instagram_post';
        postUrl: string;
        postId: string;
        mediaUrl: string;
        thumbnailUrl: string;
    }
    | {
        type: 'uploaded_asset';
        mediaUrl: string;
        thumbnailUrl: string;
        cloudinaryId: string;
        wasAiGenerated?: boolean,
        assetType: 'image' | 'video'
    }
    | {
        type: "spark_code";
        sparkCode: string;
        user_info: TikTokUserInfo;
        video_info: TikTokVideoInfo;
        item_info: TikTokItemInfo;
        mediaUrl: string;
        thumbnailUrl: string;
    }
    | {
        type: 'external_image';
        mediaUrl: string;
        thumbnailUrl: string;
        assetType: 'image';
    }


export interface CreativeVariation {
    source: CreativeSource;
    caption: string;
    cta: CallToActionType;
}

interface CheckoutDetails {
    totalCost: number;
    adsBudget: number;
    takeRate: number;
    lineItems: Array<{
        type: string;
        name: string;
        description: string;
        amount: number;
    }>;
    currency: string;
    stripeSession: {
        paid: boolean;
        payment_intent_id: string;
        client_secret: string;
    };
}

interface AgentCampaignInput {
    // Basic campaign info (agent fills)
    playbookType: PlaybookType;
    campaignName: string;
    goalLink: string;
    platform: AdPlatform;

    // Budget & schedule (agent fills - exact final format)
    budget: number;
    totalAdsBudget: number;
    budgetType: 'daily' | 'lifetime';
    length: {
        startDate: string;
        endDate: string;
    };

    // Creative variations (agent provides, maps to platform ads)
    creatives: CreativeVariation[];

    // Conversions (agent fills if applicable)
    conversionEventName?: string;
}

export interface AgentCampaignNotes {
    // Strategy-related metadata (set by setCampaignStrategy)
    strategy_summary?: string;
    one_liner?: string;
    targeted_audiences?: string[];
    targeted_locations?: string[];
    recommended_platforms?: string[];
    estimation_expectations?: string;
    budget_guidance?: string;
    strategy_updated_at?: string; // ISO date string
    strategy_phase?: 'PHASE_1' | 'PHASE_2_COMPLETE' | 'ASSETS_DISCOVERED' | 'READY_FOR_EXECUTION';
    ready_for_assets?: boolean;

    // Execution tracking metadata
    finalized_at?: string; // ISO date string
    last_updated_at?: string; // ISO date string
    execution_started_at?: string; // ISO date string
    execution_completed_at?: string; // ISO date string

    // Campaign creation tracking
    campaign_created_at?: string; // ISO date string
    
    // Error tracking
    last_error?: string;
    error_count?: number;

    // CampaignExecutor completion tracking (from tools.ts)
    executor_completed_at?: string; // ISO date string
    reasoning_summary?: string; // AI-generated summary of decisions made
    email_message?: string; // Email content generated by AI
    summary_generated_at?: string; // ISO date string
    email_sent?: boolean; // Whether completion email was sent
    email_sent_at?: string; // ISO date string
    email_error?: string; // Error message if email sending failed
    summary_generation_error?: string; // Error if summary generation failed
    error_at?: string; // ISO date string when error occurred
    error_message?: string; // General error message

    conversation_history?: any[];
    [key: string]: any; // Allow additional agent notes properties
}

interface AgentCampaign {
    id: string;
    name: string;
    playbook_type: string;
    status: string;
    goal_link: string;
    agent_notes: AgentCampaignNotes;
    campaign_input: AgentCampaignInput;
    readyForSubmission: boolean;
    checkoutDetails?: CheckoutDetails;
}

interface AssistantWorkspaceCampaignApprovalProps {
    workspaceContent: {
        type: 'campaignPreview';
        data: {
            demoCampaign?: AgentCampaign;
            isDemo?: boolean;
            scenarioId?: string;
            campaignId: string;
            isLoading?: boolean;
        };
        title?: string;
        isLoading?: boolean;
    };
    handleSendMessage: (message: string, hiddenData?: any) => void;
}

const AssistantWorkspaceCampaignApproval = ({
    workspaceContent,
    handleSendMessage,
}: AssistantWorkspaceCampaignApprovalProps) => {
    const { mobileView } = useScreen()
    const {
        currentBrand,
        temporarySessionId,
        isProBrand
    } = useBrand()

    // Integrate useCampaignBuilder
    const {
        usesExpressSetup,
        // getAutomationFee, // This will be called via useCampaignBuilder instance if needed
        automationFeeData, // Read from useCampaignBuilder state
        isLoadingAutomationFee, // Read from useCampaignBuilder state
        setCampaignDataFromAgent,
        campaignName: builderCampaignName, // Example: reading a specific field
        budget: builderBudget,
        startDate: builderStartDate,
        endDate: builderEndDate,
        adPlatform: builderAdPlatform,
        playbookType: builderPlaybookType,
        captions: builderCaptions,
        callToAction: builderCallToAction,
        uploadedAssets: builderUploadedAssets,
        selectedInstagramPosts: builderSelectedInstagramPosts,
        selectedSparkCodes: builderSelectedSparkCodes,
        // Add other state fields from useCampaignBuilder as needed for display
        previewedAsset, // If AssistantWorkspaceCampaignApproval's preview logic is to be replaced/synced
        campaignGoal: builderCampaignGoal,
        linksToPromote: builderLinksToPromote,
        geographyBuckets: builderGeographyBuckets,
        selectedAudienceGroups: builderSelectedAudienceGroups,
        conversionsEventName: builderConversionsEventName,
        conversionsEnabled: builderConversionsEnabled,
        readyForSubmission: builderReadyForSubmissionState, // Get the state
        // TODO: Ensure all necessary fields used in render methods are pulled from useCampaignBuilder
    } = useCampaignBuilder();

    const { setActiveWorkspace, chatId, sendMessage, fetchJobs, closeWorkspace } = useMaestroChat()
    const { isAuthenticated, loginWithPopup } = useAuth0()
    const { handleOpenModal } = useContext(UpgradeModalContext)

    const { selectedFacebookPage, selectedInstagramPage } = useFacebookConnections()
    const { taskUsage, refreshTaskData, fetchTaskUsage } = useMaestroTasks()

    const { data, isLoading: workspaceLoading } = workspaceContent
    const { campaignId } = data

    // Use the new signup flow store
    const { openSignupModal } = useSignupFlow()
    const { mStyle } = useScreen()

    // Signup handler function
    const handleSignUpToUnlock = async () => {
        try {
            await openSignupModal(WorkspaceType.CAMPAIGN_APPROVAL, {
                additionalStorageData: campaignId ? { 'campaign-id': campaignId } : undefined,
                chatId: chatId || undefined,
                temporarySessionId: temporarySessionId || undefined,
                loginFunction: async () => {
                    await loginWithPopup({
                        authorizationParams: {
                            screen_hint: 'signup'
                        }
                    })
                }
            })
        } catch (error) {
            console.error('Signup failed:', error)
            // Error is handled by the store, just log it here
        }
    }

    // Remove local campaign state, will use useCampaignBuilder's state
    // const [campaign, setCampaign] = useState<AgentCampaign | null>(null)
    const [isLoading, setIsLoading] = useState(true) // Keep local loading for initial fetch
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [requiresPayment, setRequiresPayment] = useState(false)
    const [selectedPreviewTab, setSelectedPreviewTab] = useState<TabItem | null>(null)
    const [paymentError, setPaymentError] = useState<string | null>(null)
    const [paymentSuccess, setPaymentSuccess] = useState(false)
    const [campaignSubmitted, setCampaignSubmitted] = useState(false) // Track when agent campaign is submitted
    const [campaignStatus, setCampaignStatus] = useState<string | null>(null) // Track campaign status from API

    // Ad preview state - moved to top level to avoid hooks violations
    const [selectedAssetIndex, setSelectedAssetIndex] = useState(0)
    const [selectedPreviewType, setSelectedPreviewType] = useState<TabItem | null>(null)

    // Payment state
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodResponse | null>(null)
    const [isLoadingPaymentMethod, setIsLoadingPaymentMethod] = useState(false)
    const [isChargingDirect, setIsChargingDirect] = useState(false)
    const [directChargeError, setDirectChargeError] = useState<string | null>(null)
    const [retryCount, setRetryCount] = useState(0)
    const [showGuestCheckout, setShowGuestCheckout] = useState(false)

    // Reusable function to fetch campaign data
    const fetchCampaignData = async (includeCheckoutDetails: boolean = true) => {
        // Check if demo campaign data is provided (for general demo scenarios or UI tests)
        if (data?.demoCampaign) {
            // Reset submitted state when loading demo campaign
            setCampaignSubmitted(false)
            setCampaignStatus(null)

            // setCampaign(data.demoCampaign) // Removed: use setCampaignDataFromAgent
            setCampaignDataFromAgent(data.demoCampaign as AgentCampaignData); // Use the new action

            // Check demo campaign status
            const status = data.demoCampaign.status;
            setCampaignStatus(status);

            // Set campaignSubmitted to true if demo status indicates it's already been submitted
            const submittedStatuses = [
                'AWAITING_AGENT_PROCESSING',
                'PROCESSING',
                'SUBMITTED',
                'ACTIVE',
                'IN_REVIEW',
                'FINISHED',
                'PAUSED',
                'STOPPED',
                'WITH_ISSUES',
                'DENIED'
            ];

            if (submittedStatuses.includes(status)) {
                console.log(`🎯 Demo campaign status "${status}" indicates already submitted, setting campaignSubmitted to true`);
                setCampaignSubmitted(true);
            }

            setIsLoading(false)

            // For UI testing, check if a specific modal should be shown
            if (data.isDemo && data.scenarioId === 'ui_test_campaign_approval_signup_modal') {
                // Ensure user is "anonymous" for signup modal test
                // This might require changes in how useBrand() handles demo mode,
                // or we simulate it here if possible. For now, we assume UpgradeModalContext handles it.
                console.log("DEMO: Triggering Campaign Approval Signup Modal");
                setShowSubscriptionFlow(true); // This should trigger the signup variant if not authenticated
            } else if (data.isDemo && data.scenarioId === 'ui_test_campaign_approval_subscription_modal') {
                // Ensure user is "authenticated" for subscription modal test for it to show plans
                console.log("DEMO: Triggering Campaign Approval Subscription Modal");
                setShowSubscriptionFlow(true); // This should trigger the plan selection if authenticated
            }
            return
        }

        if (!campaignId || !currentBrand?.id) {
            setIsLoading(false)
            return
        }

        try {
            setIsLoading(true)
            // Reset submitted state when loading new campaign
            setCampaignSubmitted(false)
            setCampaignStatus(null)

            const response = await Axios.get(`/agents/campaign/${campaignId}`, {
                params: {
                    brandId: currentBrand.id,
                    includeCheckoutDetails: includeCheckoutDetails
                }
            })

            if (response.data) {
                // setCampaign(response.data) // Removed: use setCampaignDataFromAgent
                setCampaignDataFromAgent(response.data as AgentCampaignData); // Use the new action

                // Check campaign status to determine if already submitted
                const status = response.data.status;
                setCampaignStatus(status);

                // Set campaignSubmitted to true if status indicates it's already been submitted/paid for
                const submittedStatuses = [
                    'AWAITING_AGENT_PROCESSING',
                    'PROCESSING',
                    'SUBMITTED',
                    'ACTIVE',
                    'IN_REVIEW',
                    'FINISHED',
                    'PAUSED',
                    'STOPPED',
                    'WITH_ISSUES',
                    'DENIED'
                ];

                if (submittedStatuses.includes(status)) {
                    console.log(`🎯 Campaign status "${status}" indicates already submitted, setting campaignSubmitted to true`);
                    setCampaignSubmitted(true);
                }
            } else {
                console.warn('Campaign not found or returned null')
            }
        } catch (error) {
            console.error('Failed to load campaign:', error)
        } finally {
            setIsLoading(false)
        }
    }

    // Load campaign data when component mounts or campaignId changes
    useEffect(() => {
        fetchCampaignData(true)
    }, [campaignId, currentBrand?.id, data])

    // Trigger automation fee calculation after campaign data is loaded
    const { getAutomationFee } = useCampaignBuilder();
    useEffect(() => {
        if (builderAdPlatform && builderBudget && currentBrand?.id) {
            console.log('🔄 Triggering automation fee calculation for campaign approval');
            getAutomationFee();
        }
    }, [builderAdPlatform, builderBudget, currentBrand?.id, getAutomationFee]);

    // Determine if payment is required
    useEffect(() => {
        // Payment is always required now for all campaigns
        // We just check if we have the fee data loaded
        if (!builderAdPlatform) return; // Guard against running before data is loaded into builder

        setRequiresPayment(!!automationFeeData && automationFeeData.totalCost > 0);
    }, [automationFeeData, builderAdPlatform]); // Depend on builder state

    // Load payment method information
    useEffect(() => {
        const loadPaymentMethod = async () => {
            if (!currentBrand?.id || !isAuthenticated) {
                setPaymentMethod(null);
                return;
            }

            try {
                setIsLoadingPaymentMethod(true);
                const paymentMethodData = await getDefaultPaymentMethod_api(currentBrand.id);
                setPaymentMethod(paymentMethodData);
            } catch (error) {
                console.error('Error loading payment method:', error);
                setPaymentMethod({ hasPaymentMethod: false, message: 'Error loading payment method' });
            } finally {
                setIsLoadingPaymentMethod(false);
            }
        };

        loadPaymentMethod();
    }, [currentBrand?.id, isAuthenticated]);

    // Get available preview tabs based on platform
    const getPreviewTabOptions = (platform: string): TabItem[] => {
        if (platform === 'tiktok') {
            return [
                { id: 'tiktok', title: 'TikTok' }
            ]
        }

        // Meta platform tabs
        return [
            { id: 'ig-story', title: 'Story' },
            { id: 'ig-reels', title: 'Reels' },
            { id: 'ig-feed', title: 'Feed' }
        ]
    }

    // Set default preview tab when campaign loads
    useEffect(() => {
        if (builderAdPlatform && !selectedPreviewTab) { // Use builderAdPlatform
            const tabOptions = getPreviewTabOptions(builderAdPlatform);
            if (tabOptions.length > 0) {
                const defaultTab = builderAdPlatform === 'tiktok'
                    ? tabOptions[0]
                    : tabOptions.find(tab => tab.id === 'ig-reels') || tabOptions[0];
                setSelectedPreviewTab(defaultTab);
            }
        }
    }, [builderAdPlatform, selectedPreviewTab])

    // Initialize selectedPreviewType when campaign loads
    useEffect(() => {
        if (builderAdPlatform && !selectedPreviewType) { // Use builderAdPlatform
            const tabOptions = getPreviewTabOptions(builderAdPlatform);
            if (tabOptions.length > 0) {
                const defaultTab = builderAdPlatform === 'tiktok'
                    ? tabOptions[0]
                    : tabOptions.find(tab => tab.id === 'ig-reels') || tabOptions[0];
                setSelectedPreviewType(defaultTab);
            }
        }
    }, [builderAdPlatform, selectedPreviewType])

    const handleDirectCharge = async () => {
        if (!currentBrand?.id || !builderAdPlatform || !automationFeeData) return;

        // Prevent concurrent payment attempts
        if (isChargingDirect) return;

        try {
            setIsChargingDirect(true);
            setDirectChargeError(null);

            // Get campaign data for submission
            const campaignData = useCampaignBuilder.getState().getDataForSubmission();

            const directChargeResult = await chargeCampaignDirect_api({
                brandId: currentBrand.id,
                campaignInput: campaignData,
                currency: 'USD',
                usesExpressSetup: useCampaignBuilder.getState().usesExpressSetup(),
                campaignId: campaignId || undefined
            });

            if (directChargeResult.success) {
                console.log('✅ Campaign submitted successfully via direct charge');
                console.log('🔄 Refreshing jobs to show updated campaign status...');
                await fetchJobs(); // Refresh jobs to show updated campaign status
                console.log('✅ Jobs refreshed successfully');
                handleSendMessage(`I just submitted my campaign!`);
                setActiveWorkspace(null);
            } else {
                throw new Error(directChargeResult.message || 'Direct charge failed');
            }
        } catch (error: any) {
            console.error('Direct charge failed:', error);

            // Parse error code and message from backend
            const errorMessage = error.message || 'An unexpected error occurred';
            const [errorCode, userMessage] = errorMessage.includes(':')
                ? errorMessage.split(':')
                : [null, errorMessage];

            // Handle specific error types
            switch (errorCode) {
                case 'PAYMENT_METHOD_REQUIRED':
                case 'PAYMENT_METHOD_INVALID':
                case 'STRIPE_CUSTOMER_INVALID':
                    // Payment method issues - fall back to plan selection
                    setShowCheckoutButton(false);
                    break;

                case 'CARD_DECLINED':
                case 'INSUFFICIENT_FUNDS':
                case 'CARD_EXPIRED':
                    // Card issues - show specific error and fall back
                    setDirectChargeError(userMessage);
                    setTimeout(() => {
                        setShowCheckoutButton(false);
                    }, 3000); // Show error for 3 seconds, then redirect
                    break;

                case 'AUTHENTICATION_REQUIRED':
                    // 3D Secure required - fall back to plan selection for proper flow
                    setDirectChargeError('This payment requires additional verification. Redirecting to payment form...');
                    setTimeout(() => {
                        setShowCheckoutButton(false);
                    }, 2000);
                    break;

                case 'RATE_LIMIT':
                    // Rate limited - show error and allow retry
                    setDirectChargeError('Too many requests. Please wait a moment before trying again.');
                    break;

                case 'API_ERROR':
                    // API issues - allow retry
                    setDirectChargeError('Payment system temporarily unavailable. Please try again.');
                    break;

                default:
                    // Unknown error - show generic message and allow user to choose
                    setDirectChargeError(userMessage || 'Payment failed. You can try again or use the payment form below.');
                    break;
            }
        } finally {
            setIsChargingDirect(false);
        }
    };

    const handleSubmitCampaign = async () => {
        console.log('🚀 handleSubmitCampaign called', {
            requiresPayment,
            hasPaymentMethod: paymentMethod?.hasPaymentMethod,
            automationFeeData: !!automationFeeData
        });

        // For campaigns that require payment, use direct charge
        if (requiresPayment && paymentMethod?.hasPaymentMethod) {
            console.log('🎯 Calling handleDirectCharge');
            await handleDirectCharge();
        } else if (requiresPayment && !paymentMethod?.hasPaymentMethod) {
            // No payment method, show plan selection to add one
            console.log('💳 No payment method, showing plan selection');
            setShowCheckoutButton(false);
        } else {
            // This shouldn't happen anymore since all campaigns require payment
            console.warn('Campaign submission without payment - this should not happen');
        }
    }

    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        })
    }

    const getPlatformName = (platform: string) => {
        switch (platform) {
            case 'meta': return 'Facebook & Instagram'
            case 'tiktok': return 'TikTok'
            default: return platform
        }
    }

    const renderCampaignHeader = () => {
        if (!builderCampaignName) return null; // Check against a field from builder state

        // const { campaign_input } = campaign // Old way

        return (
            <div className="campaignHeaderSection" style={sty.campaignHeaderSection}>
                <div className="campaignTitleRow" style={mStyle(sty, "campaignTitleRow")}>
                    <div className="campaignTitle" style={sty.campaignTitle}>
                        {builderCampaignName}
                    </div>
                </div>
            </div>
        )
    }

    const renderCampaignDetails = () => {
        if (!builderPlaybookType || !builderBudget || !builderAdPlatform || !builderStartDate || !builderEndDate) return null;

        // const { campaign_input, agent_notes } = campaign // Old way
        const playbookTypeName = PLAYBOOK_TYPE_INFO[builderPlaybookType]?.title;

        const detailsData = [
            {
                label: 'Link',
                value: builderLinksToPromote && builderLinksToPromote.length > 0 ? builderLinksToPromote[0].url : 'N/A',
                isLink: true
            },
            ...(playbookTypeName ? [{
                label: 'Playbook',
                value: playbookTypeName,
                isLink: false
            }] : []),
            {
                label: 'Budget',
                // builderBudget is already a string. Assuming it's the total budget.
                // Currency might be derived from automationFeeData or a default.
                value: `$${Number(builderBudget).toLocaleString()} (USD)`, // Add currency handling later
                isLink: false
            },
            {
                label: 'Platform',
                value: getPlatformName(builderAdPlatform),
                isLink: false
            },
            {
                label: 'Dates',
                value: `${formatDate(builderStartDate)} to ${formatDate(builderEndDate)}`,
                isLink: false
            },
            // Note: Locations and Target Audiences are now rendered separately as TagSymphony components
        ];

        return (
            <div className="campaignDetailsSection" style={sty.campaignDetailsSection}>
                <div className="campaignDetailsTitle" style={sty.campaignDetailsTitle}>Campaign Details</div>

                <div className="campaignDetailsBreakdown" style={sty.campaignDetailsBreakdown}>
                    {detailsData.map((item, index) => (
                        <div key={index} className="campaignDetailsRow" style={sty.campaignDetailsRow}>
                            <div className="campaignDetailsLabel" style={sty.campaignDetailsLabel}>
                                {item.label}
                            </div>
                            <div className="campaignDetailsValue" style={sty.campaignDetailsValue}>
                                {item.isLink ? (
                                    <a
                                        href={item.value}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={sty.campaignDetailsLink}
                                    >
                                        {item.value}
                                    </a>
                                ) : (
                                    item.value
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Locations as TagSymphony components */}
                    {builderGeographyBuckets && builderGeographyBuckets.length > 0 && builderGeographyBuckets[0].geographies.length > 0 && (
                        <div className="campaignDetailsTagsSection" style={sty.campaignDetailsTagsSection}>
                            <div className="campaignDetailsLabel" style={sty.campaignDetailsLabel}>
                                Locations
                            </div>
                            <div className="campaignDetailsTagsContainer" style={styx(sty.campaignDetailsTagsContainer, mobileView && sty.campaignDetailsTagsContainer_mobile)}>
                                {builderGeographyBuckets[0].geographies.map((geography, index) => (
                                    <TagSymphony
                                        key={index}
                                        className="locationTag"
                                        title={geography.name}
                                        color="green"
                                        size="medium"
                                        type="rounded"
                                        style={styx(sty.locationTag, mobileView && sty.locationTag_mobile)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Target Audiences as TagSymphony components */}
                    {builderSelectedAudienceGroups && builderSelectedAudienceGroups.length > 0 && (
                        <div className="campaignDetailsTagsSection" style={sty.campaignDetailsTagsSection}>
                            <div className="campaignDetailsLabel" style={sty.campaignDetailsLabel}>
                                Target Audience
                            </div>
                            <div className="campaignDetailsTagsContainer" style={styx(sty.campaignDetailsTagsContainer, mobileView && sty.campaignDetailsTagsContainer_mobile)}>
                                {builderSelectedAudienceGroups.map((audienceGroup, index) => (
                                    <TagSymphony
                                        key={index}
                                        className="audienceTag"
                                        title={audienceGroup.title}
                                        color="purple"
                                        size="medium"
                                        type="rounded"
                                        style={styx(sty.audienceTag, mobileView && sty.audienceTag_mobile)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    const renderAdPreview = () => {
        // Use data from useCampaignBuilder
        // Combine all creative sources from builder state to get a list for the dropdown
        const allBuilderCreatives: CreativeSelectorPost[] = [
            ...(builderUploadedAssets || []).map(asset => transformPostsToCreativeFormat([asset])[0]).filter(Boolean) as CreativeSelectorPost[],
            ...(builderSelectedInstagramPosts || []).map(post => transformPostsToCreativeFormat([post])[0]).filter(Boolean) as CreativeSelectorPost[],
            ...(builderSelectedSparkCodes || []).map(code => transformPostsToCreativeFormat([code])[0]).filter(Boolean) as CreativeSelectorPost[],
        ];

        if (!builderAdPlatform || !previewedAsset || allBuilderCreatives.length === 0) return null;

        const tabOptions = getPreviewTabOptions(builderAdPlatform);
        if (!selectedPreviewType) return null;

        // `previewedAsset` from useCampaignBuilder is already in CreativeSelectorPost format
        const currentPreviewAsset = previewedAsset;

        // Get username and profile picture based on platform
        const getUserInfo = () => {
            let username: string | null = null;
            let profilePicUrl: string | null = null;

            if (builderAdPlatform === 'meta') {
                // Check Instagram page first
                if (selectedInstagramPage?.instagramAccount?.username) {
                    username = selectedInstagramPage.instagramAccount.username;
                } else if (selectedFacebookPage?.name) {
                    // Fallback to Facebook page if no Instagram connection
                    username = selectedFacebookPage.name;
                }

                // Check Instagram profile picture first
                if (selectedInstagramPage?.instagramAccount?.profile_picture_url) {
                    profilePicUrl = selectedInstagramPage.instagramAccount.profile_picture_url;
                } else if (selectedFacebookPage?.picture?.data?.url) {
                    // Fallback to Facebook page picture if no Instagram connection
                    profilePicUrl = selectedFacebookPage.picture.data.url;
                }
            } else if (builderAdPlatform === 'tiktok' && currentPreviewAsset.originalPostType === AssetPlatformType.TIKTOK) {
                // For TikTok, only use actual user data if available
                username = (currentPreviewAsset as any).username || null;
                profilePicUrl = (currentPreviewAsset as any).profilePicUrl || null;
            }
            return { username, profilePicUrl };
        };

        const { username, profilePicUrl } = getUserInfo();
        const previewType = selectedPreviewType.id;

        // Use builderCallToAction for the CTA label
        const ctaLabel = builderCallToAction?.label || 'Learn More';

        // Use builderCaptions, ensuring it's in the Caption[] format
        // The current previewedAsset might have its own caption; decide which one to use or how to sync.
        // For now, using the caption from the previewedAsset itself if available, otherwise first builderCaption.
        const displayCaptionText = currentPreviewAsset.platformCaption || (builderCaptions.length > 0 ? builderCaptions[0].text : '');
        const captionsArray: Caption[] = [{
            id: 1, // Or derive a more stable ID
            text: displayCaptionText,
            aiGenerated: (builderCaptions.length > 0 && builderCaptions[0].aiGenerated) || false,
        }];


        const getPreviewSubtitle = () => {
            if (builderAdPlatform === 'tiktok') {
                return 'See how your ad will look on TikTok'
            }

            switch (selectedPreviewType?.id) {
                case 'ig-story':
                    return 'See how your ad will look on Instagram Stories'
                case 'ig-reels':
                    return 'See how your ad will look on Instagram Reels'
                case 'ig-feed':
                    return 'See how your ad will look on Instagram Feed'
                default:
                    return 'See how your ad will look to users'
            }
        }

        return (
            <div
                className="adPreviewSection"
                style={styx(
                    sty.adPreviewSection,
                    mobileView && sty.adPreviewSection_mobile
                )}
            >
                <div className="adPreviewHeader" style={sty.adPreviewHeader}>
                    <div className="adPreviewTitle" style={sty.adPreviewTitle}>
                        <Icon className="viewsIcon" icon="views" size={20} />
                        Ad Preview
                    </div>
                    <div className="adPreviewSubtitle" style={sty.adPreviewSubtitle}>
                        {getPreviewSubtitle()}
                    </div>

                    {/* Asset selector for multiple creatives */}
                    {allBuilderCreatives.length > 1 && (
                        <InputSymphony
                            className="adPreviewAssetSelector"
                            isSelectDropdown
                            value={selectedAssetIndex.toString()} // selectedAssetIndex now refers to index in allBuilderCreatives
                            options={allBuilderCreatives.map((creative, index) => {
                                let assetTypeDisplay: string = 'Asset';
                                if (creative.originalPostType === AssetPlatformType.INSTAGRAM) assetTypeDisplay = 'Instagram Post';
                                else if (creative.originalPostType === AssetPlatformType.TIKTOK) assetTypeDisplay = 'TikTok';
                                else if (creative.originalPostType === AssetPlatformType.CLOUDINARY) {
                                    // Check if this is an external image based on the title pattern
                                    if (creative.title?.startsWith('External Image')) {
                                        assetTypeDisplay = 'External Image';
                                    } else {
                                        assetTypeDisplay = 'Uploaded';
                                    }
                                }

                                return {
                                    title: `Ad ${index + 1} - ${assetTypeDisplay}`,
                                    value: index.toString(),
                                    description: creative.title || ''
                                } as SelectOptionItem;
                            })}
                            onOptionSelect={(option: SelectOptionItem) => {
                                const newIndex = parseInt(option.value);
                                setSelectedAssetIndex(newIndex);
                                if (allBuilderCreatives[newIndex]) {
                                    // Call setPreviewedAsset from useCampaignBuilder to update the preview
                                    useCampaignBuilder.getState().setPreviewedAsset(allBuilderCreatives[newIndex], false);
                                }
                            }}
                            style={sty.adPreviewAssetSelector}
                        />
                    )}

                    {/* Show tabs for Meta campaigns with multiple formats */}
                    {tabOptions.length > 1 && (
                        <TabsSymphony
                            isDarkBackground={true}
                            className="adPreviewTabs"
                            selectedTab={selectedPreviewType}
                            tabs={tabOptions}
                            onChange={(tab: string | TabItem) => {
                                setSelectedPreviewType(tab as TabItem)
                            }}
                            aria-label="Ad Preview Format Selector"
                        />
                    )}
                </div>

                <div
                    className="adPreviewContainer"
                    style={styx(
                        sty.adPreviewContainer,
                        previewType === 'ig-feed' ? sty.adPreviewContainer_feed :
                            (previewType === 'ig-story' || previewType === 'ig-reels' || previewType === 'tiktok') ? sty.adPreviewContainer_vertical :
                                sty.adPreviewContainer
                    )}
                >
                    <AdPreview
                        className="campaignPreviewAdPreview"
                        username={username}
                        callToActionLabel={ctaLabel}
                        profilePicUrl={profilePicUrl}
                        previewedAsset={currentPreviewAsset}
                        captions={captionsArray}
                        previewType={previewType}
                        style={styx(
                            sty.adPreviewComponent,
                            previewType === 'ig-feed' ? sty.adPreviewComponent_feed :
                                (previewType === 'ig-story' || previewType === 'ig-reels' || previewType === 'tiktok') ? sty.adPreviewComponent_vertical :
                                    sty.adPreviewComponent
                        )}
                    />
                </div>
            </div>
        )
    }

    // Subscription flow state - keeping for signup modal but plan selection now goes in actions
    const [showSubscriptionFlow, setShowSubscriptionFlow] = useState(false)

    // Two-stage checkout: first show button, then show plan selection
    const [showCheckoutButton, setShowCheckoutButton] = useState(true)

    // Auto-open subscription modal on page load if URL param is set
    // Note: With new architecture, authenticated users will see plan selection in actions automatically
    // This is mainly for unauthenticated users who need to sign up first
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const autoOpenSubscription = urlParams.get('autoOpenSubscription');

        // Check builderAdPlatform to ensure campaign data is loaded before triggering
        if (autoOpenSubscription === 'true' && builderAdPlatform) {
            if (!isAuthenticated) {
                // Unauthenticated users see signup flow
                if (!showSubscriptionFlow) {
                    console.log('🔄 Auto-opening subscription signup modal after page load');
                    setShowSubscriptionFlow(true);
                }
            } else {
                // Authenticated users skip to plan selection
                console.log('🔄 Auto-opening plan selection for authenticated user');
                setShowCheckoutButton(false);
            }

            // Clean up the URL parameter
            urlParams.delete('autoOpenSubscription');
            const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, [builderAdPlatform, showSubscriptionFlow, isAuthenticated])

    // Clear direct charge error when switching views
    useEffect(() => {
        if (!showCheckoutButton) {
            setDirectChargeError(null);
        }
    }, [showCheckoutButton])

    // Auto-clear errors after 10 seconds
    useEffect(() => {
        if (directChargeError) {
            const timer = setTimeout(() => {
                setDirectChargeError(null);
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [directChargeError])

    // Mobile tabs for Details/Ad Preview
    const [selectedMobileTab, setSelectedMobileTab] = useState<'details' | 'preview'>('details')
    const mobileTabOptions: TabItem[] = [
        { id: 'details', title: 'Details' },
        { id: 'preview', title: 'Ad Preview' }
    ]

    // Use builderAdPlatform (or another core builder field) to check if data is loaded
    if (isLoading || !builderAdPlatform) {
        const showNotFound = !isLoading && campaignId && currentBrand?.id && !builderAdPlatform

        return (
            <div className="assistantWorkspaceCampaignApprovalLoading" style={sty.loadingContainer}>
                <Icon
                    icon="maestroBadgeImage"
                    size={48}
                    className="loadingIcon"
                    style={sty.loadingIcon}
                />
                <div style={sty.loadingText}>
                    {isLoading ? 'Loading campaign details...' :
                        showNotFound ? 'Campaign not found' : // This condition might need refinement
                            'Campaign data unavailable'}
                </div>
            </div>
        )
    }

    const isDirectSubmission = !requiresPayment;
    // Assuming readyForSubmission was part of AgentCampaign and mapped to useCampaignBuilder state
    // If not, it needs to be added to setCampaignDataFromAgent and to CampaignState
    // For now, let's assume it's available as, e.g., `builderReadyForSubmission`
    // If it's not on builder state, this will be true by default or needs another source.
    // Let's default to true if not explicitly set by agent data, assuming if we got this far, it's ready.
    const isReadyForSubmission = builderReadyForSubmissionState === undefined ? true : builderReadyForSubmissionState;


    const getCampaignStatusIcon = () => {
        switch (campaignStatus) {
            case 'AWAITING_AGENT_PROCESSING':
                return 'clock'
            case 'PROCESSING':
                return 'loading'
            case 'ACTIVE':
                return 'boost'
            case 'IN_REVIEW':
                return 'views'
            case 'FINISHED':
                return 'check'
            case 'PAUSED':
                return 'pause'
            case 'STOPPED':
                return 'stop'
            case 'WITH_ISSUES':
                return 'warning'
            case 'DENIED':
                return 'close'
            default:
                return 'check'
        }
    }

    const getCampaignStatusTitle = () => {
        switch (campaignStatus) {
            case 'AWAITING_AGENT_PROCESSING':
                return 'Campaign Awaiting Processing'
            case 'PROCESSING':
                return 'Campaign Being Processed'
            case 'ACTIVE':
                return 'Campaign is Live'
            case 'IN_REVIEW':
                return 'Campaign Under Review'
            case 'FINISHED':
                return 'Campaign Completed'
            case 'PAUSED':
                return 'Campaign Paused'
            case 'STOPPED':
                return 'Campaign Stopped'
            case 'WITH_ISSUES':
                return 'Campaign Needs Attention'
            case 'DENIED':
                return 'Campaign Not Approved'
            default:
                return 'Campaign Submitted Successfully'
        }
    }

    const getStatusMessage = () => {
        console.log('campaignSubmitted', campaignSubmitted);
        console.log('campaignStatus', campaignStatus);
        console.log('isAuthenticated', isAuthenticated);
        console.log('requiresPayment', requiresPayment);
        console.log('showCheckoutButton', showCheckoutButton);
        console.log('automationFeeData', automationFeeData);
        console.log('paymentMethod', paymentMethod);
        console.log('isLoadingAutomationFee', isLoadingAutomationFee);
        console.log('isSubmitting', isSubmitting);
        if (campaignSubmitted) {
            // Provide specific message based on campaign status
            switch (campaignStatus) {
                case 'AWAITING_AGENT_PROCESSING':
                    return 'Your campaign has been paid for and is awaiting processing by our agents. We\'ll set up targeting and launch it for you.'
                case 'PROCESSING':
                    return 'Your campaign is currently being processed by our agents. We\'ll notify you when it\'s ready to go live.'
                case 'ACTIVE':
                    return 'Your campaign is live and running! You can view performance in the Marketing tab.'
                case 'IN_REVIEW':
                    return 'Your campaign is under review by the ad platform. This typically takes 24-48 hours.'
                case 'FINISHED':
                    return 'Your campaign has completed successfully. View results in the Marketing tab.'
                case 'PAUSED':
                    return 'Your campaign is currently paused. You can resume it from the Marketing tab.'
                case 'STOPPED':
                    return 'Your campaign has been stopped. View final results in the Marketing tab.'
                case 'WITH_ISSUES':
                    return 'Your campaign needs attention. Please check the Marketing tab for details.'
                case 'DENIED':
                    return 'Your campaign was not approved by the ad platform. Please contact support for assistance.'
                default:
                    return 'Your campaign has been submitted and is being reviewed by our agents. We\'ll notify you when it\'s ready to go live.'
            }
        }
        if (isAuthenticated && requiresPayment && showCheckoutButton && automationFeeData) {
            // Create budget breakdown message
            const totalCost = automationFeeData.totalCost;
            const adsBudget = automationFeeData.adsBudget;
            const paymentMethodText = paymentMethod?.hasPaymentMethod && paymentMethod.paymentMethod?.card
                ? `**** ${paymentMethod.paymentMethod.card.last4} (${paymentMethod.paymentMethod.card.brand.toUpperCase()})`
                : 'your payment method';

            return `Total cost: $${totalCost.toFixed(2)} ($${adsBudget.toFixed(2)} ads budget + fees). Will be charged to ${paymentMethodText}.`;
        }
        if (isAuthenticated && requiresPayment && !showCheckoutButton) {
            return 'Choose a plan below to launch your campaign.'
        }
        if (isAuthenticated && requiresPayment) {
            return 'Review the cost breakdown below and click Launch Campaign to proceed.'
        }
        return 'Once you signup, we\'ll bring you back to your chat so you can finish launching.'
    }

    // Determine button state and actions based on current conditions
    const getButtonConfig = () => {
        if (campaignSubmitted) {
            return {
                text: 'Continue Chatting',
                onClick: () => {
                    closeWorkspace();
                },
                // disabled: true
            }
        }
        if (isSubmitting || isChargingDirect) {
            return {
                text: isChargingDirect ? 'Processing Payment...' : 'Submitting...',
                onClick: () => { },
                disabled: true
            }
        }

        // Check authentication first - anonymous users should be able to checkout as guest or sign up
        if (!isAuthenticated) {
            // Return array of button configs for anonymous users
            return [
                // {
                //     text: 'Checkout as Guest',
                //     onClick: () => setShowGuestCheckout(true),
                //     disabled: false,
                //     variant: 'primary' as const,
                //     icon: "creditCard"
                // },
                {
                    text: 'Sign Up to Submit',
                    onClick: handleSignUpToUnlock,
                    disabled: false,
                    variant: 'primary' as const,
                    icon: "user"
                }
            ]
        }

        if (!isReadyForSubmission) {
            return {
                text: 'Campaign Not Ready',
                onClick: () => { },
                disabled: true
            }
        }

        // For authenticated users with payment requirements in plan selection stage
        if (requiresPayment) {
            if (!showCheckoutButton) {
                // Return null since plan selection will be shown via showCustomContent
                return null
            }

            if(isLoadingPaymentMethod) {
                return {
                    text: 'Loading...',
                    onClick: () => { },
                    disabled: true
                }
            }

            if (paymentMethod?.hasPaymentMethod) {
                return {
                    text: 'Launch Campaign',
                    onClick: handleSubmitCampaign,
                    disabled: isLoadingPaymentMethod || !automationFeeData
                }
            } else {
                return {
                    text: 'Launch Campaign',
                    onClick: () => {
                        setShowCheckoutButton(false) // Switch to plan selection view
                    },
                    disabled: false
                }
            }
        }


        // Default case - shouldn't happen for campaigns
        return {
            text: 'Launch Campaign',
            onClick: handleSubmitCampaign,
            disabled: false
        }
    }

    const buttonConfig = getButtonConfig()

    const actionButtons: ActionButton[] = Array.isArray(buttonConfig) ?
        buttonConfig.map(config => ({
            text: config.text,
            onClick: config.onClick,
            variant: config.variant || 'primary' as const,
            icon: config.icon || "check",
            disabled: config.disabled,
            loading: isSubmitting
        })) :
        buttonConfig ? [
            {
                text: buttonConfig.text,
                onClick: buttonConfig.onClick,
                variant: 'primary' as const,
                icon: "check",
                disabled: buttonConfig.disabled || isLoadingAutomationFee, // isLoadingAutomationFee from useCampaignBuilder
                loading: isSubmitting
            }
        ] : []



    // Handle successful agent campaign payment
    const handleAgentCampaignPaid = async (paymentResult: ConfirmPaymentResponse) => {
        if (paymentResult.success) {
            // Send chat message about campaign submission
            try {
                sendMessage("I just submitted my campaign!");
                console.log('✅ Sent chat message: Campaign submitted');
            } catch (error) {
                console.error('Failed to send chat message:', error);
                // Don't fail the flow if chat message fails
            }

            // Refresh campaign data to get updated status
            if (campaignId && currentBrand?.id) {
                try {
                    console.log('🔄 Refreshing campaign data after payment...');
                    await fetchCampaignData(false);
                    console.log('✅ Campaign data refreshed successfully');
                } catch (error) {
                    console.error('Failed to refresh campaign data:', error);
                    // Don't fail the flow if campaign refresh fails
                }
            }

            // Refresh task usage after payment (task was consumed + possibly subscription upgraded)
            if (currentBrand?.id) {
                try {
                    // Refresh jobs to show updated campaign status
                    console.log('🔄 Refreshing jobs to show updated campaign status...');
                    await fetchJobs();
                    console.log('✅ Jobs refreshed successfully');

                    console.log('✅ Refreshed task usage and jobs after payment');

                } catch (taskError) {
                    console.error('Failed to refresh task usage after payment:', taskError);
                    // Don't fail the flow if task refresh fails
                }
            }

            // Hide checkout UI and show success UI
            setShowCheckoutButton(true); // Reset to default state
            setShowGuestCheckout(false); // Hide guest checkout if it was shown

            // Set submitted state to update UI and show "Continue Chatting" button
            setCampaignSubmitted(true);

            console.log('✅ Agent campaign submitted successfully');
        }
    };

    // Renders plan selection for authenticated users in the actions area
    const renderPlanSelectionForActions = () => {
        if (!isAuthenticated || !requiresPayment || campaignSubmitted || showCheckoutButton) return null;

        // Get the campaign data and add chat_id to identify this as an agent campaign
        const campaignDataWithChatId = {
            ...(useCampaignBuilder.getState().getDataForSubmission() || {}),
            chat_id: chatId // Add chat_id so backend knows this is an agent campaign
        };

        return (
            <div className="planSelectionWithBackButton">
                <div className="planSelectionHeader" style={sty.planSelectionHeader}>
                    <ButtonSymphony
                        className="backToCampaignButton"
                        variant="text"
                        onClick={() => setShowCheckoutButton(true)}
                        style={sty.backButton}
                    >
                        <Icon className="backIcon" icon="arrowLeft" size={16} />
                        Back
                    </ButtonSymphony>
                </div>
                <CampaignCheckoutView
                    showHeaders={false}
                    className="campaignPreviewPlanSelection"
                    isCampaignBundle={true}
                    onPaid={handleAgentCampaignPaid}
                    overrideCampaignInput={campaignDataWithChatId}
                    existingCampaignId={campaignId} // Pass the existing campaign ID
                    onCancelPayment={() => {
                        // Go back to campaign details view
                        setShowCheckoutButton(true);
                    }}
                />
            </div>
        );
    };

    // Renders guest checkout form for anonymous users
    const renderGuestCheckoutForActions = () => {
        if (isAuthenticated || !showGuestCheckout) return null;

        // Get the campaign data for guest checkout
        const campaignData = useCampaignBuilder.getState().getDataForSubmission();

        if (!campaignData) return null;

        return (
            <div className="guestCheckoutWithBackButton">
                <div className="guestCheckoutHeader" style={sty.planSelectionHeader}>
                    <ButtonSymphony
                        className="backToCampaignButton"
                        variant="text"
                        onClick={() => setShowGuestCheckout(false)}
                        style={sty.backButton}
                    >
                        <Icon className="backIcon" icon="arrowLeft" size={16} />
                        Back
                    </ButtonSymphony>
                </div>
                <UnifiedPaymentForm
                    isDarkBackground={true}
                    isAnonymous={true}
                    temporarySessionId={temporarySessionId || ''}
                    paymentData={{
                        type: 'campaign',
                        campaignInput: campaignData,
                        currency: 'USD',
                        campaignId: campaignId || ''
                    }}
                    onSuccess={handleAgentCampaignPaid}
                    onError={(error) => {
                        console.error('Guest checkout error:', error);

                        // Handle specific error cases
                        if (error.includes('account with this email already exists')) {
                            // Show specific message for existing users
                            showToast({
                                message: 'An account with this email already exists. Please sign in or use a different email.',
                                error: true,
                            });
                        } else {
                            // Generic error handling
                            showToast({
                                message: error || 'Payment failed. Please try again.',
                                error: true,
                            });
                        }
                    }}
                    submitButtonText="Submit Campaign"
                />
            </div>
        );
    };

    // Render fee breakdown and payment method info
    const renderFeeBreakdown = () => {
        if (!automationFeeData || !requiresPayment) return null;

        const formatCurrency = (amount: number) => {
            return amount.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        };

        return (
            <div className="feeBreakdownSection" style={sty.feeBreakdownSection}>
                <div className="feeBreakdownTitle" style={sty.feeBreakdownTitle}>
                    <Icon className="costIcon" icon="dollarSign" size={20} />
                    Campaign Cost
                </div>

                <div className="feeBreakdownItems" style={sty.feeBreakdownItems}>
                    {automationFeeData.lineItems?.map((item, index) => (
                        <div key={index} className="feeBreakdownItem" style={sty.feeBreakdownItem}>
                            <div className="feeItemDetails" style={sty.feeItemDetails}>
                                <div className="feeItemName" style={sty.feeItemName}>{item.name}</div>
                                <div className="feeItemDescription" style={sty.feeItemDescription}>{item.description}</div>
                            </div>
                            <div className="feeItemAmount" style={sty.feeItemAmount}>
                                {formatCurrency(item.amount)}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="feeBreakdownTotal" style={sty.feeBreakdownTotal}>
                    <div className="feeTotalLabel" style={sty.feeTotalLabel}>Total</div>
                    <div className="feeTotalAmount" style={sty.feeTotalAmount}>
                        {formatCurrency(automationFeeData.totalCost)}
                    </div>
                </div>

                {/* Payment method info */}
                {/* {isAuthenticated && (
                    <div className="paymentMethodSection" style={sty.paymentMethodSection}>
                        {isLoadingPaymentMethod ? (
                            <div className="paymentMethodLoading" style={sty.paymentMethodLoading}>
                                <Icon className="loadingIcon" icon="loading" size={16} />
                                Loading payment method...
                            </div>
                        ) : paymentMethod?.hasPaymentMethod && paymentMethod.paymentMethod?.card ? (
                            <div className="paymentMethodInfo" style={sty.paymentMethodInfo}>
                                <Icon className="creditCardIcon" icon="billing" size={16} />
                                <span>Will be charged to: **** {paymentMethod.paymentMethod.card.last4} ({paymentMethod.paymentMethod.card.brand.toUpperCase()})</span>
                            </div>
                        ) : (
                            <div className="paymentMethodWarning" style={sty.paymentMethodWarning}>
                                <Icon className="warningIcon" icon="warning" size={16} />
                                <span>No payment method on file</span>
                            </div>
                        )}

                        {directChargeError && (
                            <div className="directChargeError" style={sty.directChargeError}>
                                <Icon className="errorIcon" icon="warning" size={16} />
                                <span>{directChargeError}</span>
                            </div>
                        )}
                    </div>
                )} */}
            </div>
        );
    };




    return (
        <div
            className={"assistantWorkspaceCampaignApprovalContainer"}
            style={mStyle(sty, "assistantWorkspaceCampaignApprovalContainer")}
        >
            {/* Signup modal is handled globally by the SignupFlowModal component */}

            {/* Main content - always shown */}
            <div className="containerContent" style={mStyle(sty, "containerContent")}>
                {renderCampaignHeader()}
                {mobileView ? (
                    <>
                        <TabsSymphony
                            isDarkBackground
                            className="mobileTabs"
                            selectedTab={mobileTabOptions.find(tab => tab.id === selectedMobileTab) || mobileTabOptions[0]}
                            tabs={mobileTabOptions}
                            onChange={(tab: string | TabItem) => {
                                const tabItem = tab as TabItem
                                setSelectedMobileTab(tabItem.id as 'details' | 'preview')
                            }}
                            style={sty.mobileTabs}
                        />
                        {selectedMobileTab === 'details' ? (
                            <>
                                {renderCampaignDetails()}
                                {renderFeeBreakdown()}
                            </>
                        ) : renderAdPreview()}
                    </>
                ) : (
                    <div className="desktopContentGrid" style={sty.desktopContentGrid}>
                        <div className="leftColumn" style={sty.leftColumn}>
                            {renderCampaignDetails()}
                            {renderFeeBreakdown()}
                        </div>
                        {renderAdPreview()}
                    </div>
                )}
            </div>

            {/* Actions area - shows either buttons, plan selection, or submitted state */}
            {builderAdPlatform && (
                <AssistantWorkspaceActions
                    icon={campaignSubmitted ? getCampaignStatusIcon() : (isReadyForSubmission ? "boost" : "clock")}
                    title={campaignSubmitted ? getCampaignStatusTitle() : (isReadyForSubmission ? 'Launch Your Campaign' : 'Campaign In Progress')}
                    description={getStatusMessage()}
                    buttons={actionButtons}
                    sticky={true}
                    customContent={showGuestCheckout ? renderGuestCheckoutForActions() : renderPlanSelectionForActions()}
                    showCustomContent={
                        (!isAuthenticated && showGuestCheckout) ||
                        (isAuthenticated && requiresPayment && isReadyForSubmission && !campaignSubmitted && !showCheckoutButton)
                    }
                />
            )}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    assistantWorkspaceCampaignApprovalContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: 0,
        width: '100%',
        minHeight: 500,
        backgroundColor: WorkspaceTheme.primary,
    },
    assistantWorkspaceCampaignApprovalContainer_mobile: {
        gap: 20,
    },

    containerContent: {
        padding: 0,
    },

    containerContent_mobile: {
        padding: 16,
    },

    campaignHeaderSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        paddingBottom: 20,
        borderBottom: `1px solid ${WorkspaceTheme.border}`,
    },
    campaignTitleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },

    campaignTitleRow_mobile: {
        flexDirection: 'column',
    },
    campaignTitle: {
        ...Sty.text24bold,
        color: WorkspaceTheme.textPrimary,
        flex: 1,
    },
    campaignGoal: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
    },
    goalLink: {
        color: WorkspaceTheme.accent,
        textDecoration: 'none',
        fontWeight: 500,
    },
    strategyStatement: {
        padding: 16,
        backgroundColor: WorkspaceTheme.surface,
        borderRadius: 8,
        border: `1px solid ${WorkspaceTheme.border}`,
    },
    strategyLabel: {
        ...Sty.text14bold,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 8,
    },
    strategyText: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        lineHeight: 1.5,
        fontStyle: 'italic',
    },

    campaignOverview: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 20,
    },
    campaignOverview_mobile: {
        gridTemplateColumns: '1fr',
    },

    overviewCard: {
        padding: 20,
        border: `1px solid ${WorkspaceTheme.border}`,
        borderRadius: 12,
        backgroundColor: WorkspaceTheme.surface,
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
    },
    cardTitle: {
        ...Sty.text16bold,
        color: WorkspaceTheme.textPrimary,
    },

    platformInfo: {
        marginBottom: 12,
    },
    platformName: {
        fontSize: 16,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
    },
    budgetInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: 12,
    },
    budgetAmount: {
        ...Sty.text20bold,
        color: WorkspaceTheme.accent,
    },
    budgetType: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
    },
    campaignDates: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    dateItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        color: WorkspaceTheme.textSecondary,
    },

    audienceDescription: {
        fontSize: 14,
        color: WorkspaceTheme.textPrimary,
        lineHeight: 1.4,
        marginBottom: 12,
    },
    audienceGeographies: {
        marginBottom: 12,
    },
    geographiesLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: WorkspaceTheme.textSecondary,
        marginBottom: 6,
    },
    geographiesList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
    },
    geoTag: {
        backgroundColor: WorkspaceTheme.accentSoft,
        color: WorkspaceTheme.accent,
        border: `1px solid ${WorkspaceTheme.border}`,
        fontSize: 11,
    },
    geoOverflow: {
        fontSize: 11,
        color: WorkspaceTheme.textTertiary,
        fontStyle: 'italic',
    },
    audienceTargets: {},
    targetsLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: WorkspaceTheme.textSecondary,
        marginBottom: 6,
    },
    targetsList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
    },
    targetTag: {
        backgroundColor: WorkspaceTheme.accentSoft,
        color: WorkspaceTheme.accent,
        border: `1px solid ${WorkspaceTheme.border}`,
        fontSize: 11,
    },
    targetOverflow: {
        fontSize: 11,
        color: WorkspaceTheme.textTertiary,
        fontStyle: 'italic',
    },

    creativesSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    creativesHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    creativesTitle: {
        ...Sty.text18bold,
        color: WorkspaceTheme.textPrimary,
    },
    creativesList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 16,
    },
    creativesList_mobile: {
        gridTemplateColumns: '1fr',
    },
    creativeCard: {
        border: `1px solid ${WorkspaceTheme.border}`,
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: WorkspaceTheme.surface,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        transition: 'all 0.2s ease',
    },
    creativePreview: {
        position: 'relative',
        width: '100%',
        height: 200,
        overflow: 'hidden',
    },
    videoPreview: {
        position: 'relative',
        width: '100%',
        height: '100%',
    },
    creativeImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    videoPlayOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: '50%',
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    playIcon: {
        color: 'white',
        marginLeft: 2,
    },
    creativeDetails: {
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        backgroundColor: WorkspaceTheme.surface,
    },
    creativeCaption: {
        fontSize: 14,
        color: WorkspaceTheme.textPrimary,
        lineHeight: 1.4,
    },
    creativeCTA: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: WorkspaceTheme.textSecondary,
    },

    campaignActionsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        paddingTop: 24,
        borderTop: `1px solid ${WorkspaceTheme.border}`,
        backgroundColor: WorkspaceTheme.surface,
        padding: 24,
        borderRadius: 12,
        marginTop: 8,
    },
    actionsHeader: {
        textAlign: 'center',
    },
    actionsTitle: {
        ...Sty.text20bold,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 8,
    },
    actionsSubtitle: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        lineHeight: 1.4,
    },
    actionButtonsRow: {
        display: 'flex',
        gap: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    keepTweakingButton: {
        padding: '12px 24px',
        borderColor: WorkspaceTheme.textSecondary,
        color: WorkspaceTheme.textSecondary,
    },
    submitCampaignButton: {
        padding: '12px 32px',
        backgroundColor: WorkspaceTheme.accent,
        fontSize: 16,
        fontWeight: 600,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },

    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 60,
        gap: 20,
        backgroundColor: WorkspaceTheme.primary,
    },
    loadingIcon: {
        color: WorkspaceTheme.accent,
        animation: 'spin 2s linear infinite',
    },
    loadingText: {
        fontSize: 16,
        color: WorkspaceTheme.textSecondary,
        textAlign: 'center',
    },

    pricingSection: {
        marginTop: 24,
        marginBottom: 16,
        padding: 20,
        backgroundColor: WorkspaceTheme.surface,
        borderRadius: 12,
        border: `1px solid ${WorkspaceTheme.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    pricingTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 16,
    },
    pricingBreakdown: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    pricingRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 12,
        borderBottom: `1px solid ${WorkspaceTheme.border}`,
    },
    pricingRowTotal: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        backgroundColor: WorkspaceTheme.accentSoft,
        borderRadius: 8,
        marginTop: 8,
        border: `1px solid ${WorkspaceTheme.border}`,
    },
    pricingLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: WorkspaceTheme.textPrimary,
    },
    pricingLabelTotal: {
        fontSize: 16,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
    },
    pricingLabelWithDesc: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        flex: 1,
        marginRight: 16,
    },
    pricingDescription: {
        fontSize: 12,
        color: WorkspaceTheme.textSecondary,
        lineHeight: 1.4,
    },
    pricingValue: {
        fontSize: 14,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
    },
    pricingValueTotal: {
        fontSize: 16,
        fontWeight: 700,
        color: WorkspaceTheme.accent,
    },
    loadingPricing: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 20,
        justifyContent: 'center',
        color: WorkspaceTheme.textSecondary,
        fontSize: 14,
    },

    adPreviewSection: {
        marginTop: 24,
        padding: 20,
        backgroundColor: WorkspaceTheme.surface,
        borderRadius: 12,
        border: `1px solid ${WorkspaceTheme.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        width: '100%',
        boxSizing: 'border-box',
    },
    adPreviewSection_mobile: {
        marginTop: 0,
        width: '100%',
    },
    adPreviewHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginBottom: 20,
    },
    adPreviewTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 18,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
    },
    adPreviewSubtitle: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
    },
    adPreviewAssetSelector: {
        marginBottom: 12,
    },
    adPreviewContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '0 auto',
        minHeight: 400,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },
    adPreviewContainer_vertical: {
        maxWidth: 300,
        width: '100%',
    },
    adPreviewContainer_feed: {
        maxWidth: 400,
        width: '100%',
    },
    adPreviewComponent: {
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        maxWidth: '100%',
    },
    adPreviewComponent_vertical: {
        width: '100%',
        maxWidth: 300,
        aspectRatio: '9/16',
    },
    adPreviewComponent_feed: {
        width: '100%',
        maxWidth: 400,
    },

    missingFieldsContainer: {
        marginTop: 16,
        padding: 16,
        backgroundColor: Colors.red + '15',
        borderRadius: 8,
        border: `1px solid ${Colors.red}40`,
    },
    missingFieldsTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: Colors.red,
        marginBottom: 8,
    },
    missingFieldsList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    submitCampaignButton_disabled: {
        backgroundColor: WorkspaceTheme.textTertiary,
        cursor: 'not-allowed',
    },

    postPreview: {
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    postPlaceholder: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 20,
        textAlign: 'center',
    },
    postIcon: {
        color: WorkspaceTheme.accent,
    },
    postLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
    },
    postLink: {
        fontSize: 11,
        color: WorkspaceTheme.accent,
        textDecoration: 'none',
        fontWeight: 500,
    },

    adPreviewPlaceholder: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 40,
        backgroundColor: WorkspaceTheme.surface,
        borderRadius: 12,
        textAlign: 'center',
        minHeight: 200,
        border: `1px solid ${WorkspaceTheme.border}`,
    },
    placeholderText: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        lineHeight: 1.4,
    },
    placeholderLink: {
        fontSize: 14,
        color: WorkspaceTheme.accent,
        textDecoration: 'none',
        fontWeight: 500,
    },

    checkoutSection: {
        marginTop: 24,
        marginBottom: 16,
        padding: 24,
        backgroundColor: WorkspaceTheme.surface,
        borderRadius: 12,
        border: `1px solid ${WorkspaceTheme.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    checkoutTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 20,
        textAlign: 'center',
    },
    checkoutSubtitle: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        textAlign: 'center',
    },
    checkoutHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'center',
    },
    checkoutForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        alignItems: 'center',
    },
    paymentElementContainer: {
        width: '100%',
        maxWidth: 400,
    },
    submitPaymentButton: {
        backgroundColor: WorkspaceTheme.accent,
        fontSize: 16,
        fontWeight: 600,
        padding: '12px 24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
    paymentSecurity: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: WorkspaceTheme.textSecondary,
        fontSize: 14,
    },
    successIcon: {
        color: Colors.green,
    },
    successTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: Colors.green,
    },
    successDescription: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        textAlign: 'center',
    },
    paymentErrorBanner: {
        marginBottom: 16,
    },
    paymentSuccessContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: 32,
        textAlign: 'center',
    },

    paypalCheckoutSection: {
        marginTop: 24,
        marginBottom: 16,
        padding: 24,
        backgroundColor: WorkspaceTheme.surface,
        borderRadius: 12,
        border: `1px solid ${WorkspaceTheme.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    simulatedCheckoutContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        alignItems: 'center',
        padding: 20,
        backgroundColor: WorkspaceTheme.accentSoft,
        borderRadius: 8,
        border: `1px solid ${WorkspaceTheme.border}`,
    },
    totalAmount: {
        fontSize: 20,
        fontWeight: 700,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 8,
    },
    simulateCheckoutButton: {
        backgroundColor: WorkspaceTheme.accent,
        fontSize: 16,
        fontWeight: 600,
        padding: '12px 24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
    checkoutNote: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: WorkspaceTheme.textSecondary,
        fontSize: 12,
        fontStyle: 'italic',
    },

    campaignDetailsSection: {
        marginTop: 24,
        padding: 20,
        backgroundColor: WorkspaceTheme.surface,
        borderRadius: 12,
        border: `1px solid ${WorkspaceTheme.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        width: '100%',
        boxSizing: 'border-box',
    },
    campaignDetailsTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 16,
    },
    campaignDetailsBreakdown: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    campaignDetailsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 12,
        borderBottom: `1px solid ${WorkspaceTheme.border}`,
    },
    campaignDetailsLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: WorkspaceTheme.textPrimary,
    },
    campaignDetailsValue: {
        fontSize: 14,
        fontWeight: 400,
        color: WorkspaceTheme.textPrimary,
        flex: 1,
        textAlign: 'right',
        wordBreak: 'break-word',
    },
    campaignDetailsLink: {
        color: WorkspaceTheme.accent,
        textDecoration: 'none',
        fontWeight: 500,
    },

    desktopContentGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 24,
        width: '100%',
        boxSizing: 'border-box',
    },

    leftColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },

    feeBreakdownSection: {
        marginTop: 24,
        padding: 20,
        backgroundColor: WorkspaceTheme.surface,
        borderRadius: 12,
        border: `1px solid ${WorkspaceTheme.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        width: '100%',
        boxSizing: 'border-box',
    },

    feeBreakdownTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 18,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 16,
    },

    feeBreakdownItems: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginBottom: 16,
    },

    feeBreakdownItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 12,
        borderBottom: `1px solid ${WorkspaceTheme.border}`,
    },

    feeItemDetails: {
        flex: 1,
        marginRight: 16,
    },

    feeItemName: {
        fontSize: 14,
        fontWeight: 500,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 4,
    },

    feeItemDescription: {
        fontSize: 12,
        color: WorkspaceTheme.textSecondary,
        lineHeight: 1.4,
    },

    feeItemAmount: {
        fontSize: 14,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        textAlign: 'right',
    },

    feeBreakdownTotal: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        backgroundColor: WorkspaceTheme.accentSoft,
        borderRadius: 8,
        marginBottom: 16,
        border: `1px solid ${WorkspaceTheme.border}`,
    },

    feeTotalLabel: {
        fontSize: 16,
        fontWeight: 600,
        color: 'black',
    },

    feeTotalAmount: {
        fontSize: 18,
        fontWeight: 700,
        color: WorkspaceTheme.accent,
    },

    paymentMethodSection: {
        padding: 12,
        backgroundColor: WorkspaceTheme.primary,
        borderRadius: 8,
        border: `1px solid ${WorkspaceTheme.border}`,
    },

    paymentMethodLoading: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
    },

    paymentMethodInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: WorkspaceTheme.textPrimary,
    },

    paymentMethodWarning: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: Colors.red,
    },

    directChargeError: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: Colors.red,
        marginTop: 8,
        padding: 8,
        backgroundColor: Colors.red + '15',
        borderRadius: 4,
        border: `1px solid ${Colors.red}40`,
    },

    mobileTabs: {
        marginTop: 16,
        marginBottom: 16,
        width: '100%',
    },

    planSelectionHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: `1px solid ${WorkspaceTheme.border}`,
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: WorkspaceTheme.textSecondary,
        fontSize: 14,
        padding: '8px 0',
    },

    campaignDetailsTagsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        paddingTop: 12,
    },

    campaignDetailsTagsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'flex-start',
    },

    campaignDetailsTagsContainer_mobile: {
        gap: 6,
    },

    locationTag: {
        flexShrink: 0,
    },

    locationTag_mobile: {
        flexShrink: 1,
        maxWidth: 'none',
        minWidth: 'auto',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
        padding: '8px 12px',
        lineHeight: 1.3,
    },

    audienceTag: {
        flexShrink: 0,
    },

    audienceTag_mobile: {
        flexShrink: 1,
        maxWidth: 'none',
        minWidth: 'auto',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
        padding: '8px 12px',
        lineHeight: 1.3,
    },

}

export default AssistantWorkspaceCampaignApproval 